import { motion } from "framer-motion";
import { Shield, Bot, BarChart3, Target, Lock, Zap } from "lucide-react";
import MovingBackground from "./MovingBackground";

const ease = [0.16, 1, 0.3, 1] as const;

const pillars = [
  { icon: Bot, label: "Copilot Readiness", desc: "Assess & prepare your environment", color: "hsl(var(--primary))" },
  { icon: Shield, label: "AI Governance", desc: "Policies, compliance & oversight", color: "hsl(var(--success))" },
  { icon: Zap, label: "Agent Adoption", desc: "Deploy & scale AI agents", color: "hsl(var(--primary-glow))" },
  { icon: Target, label: "Agent Strategy", desc: "Roadmap for AI-driven workflows", color: "hsl(var(--primary))" },
  { icon: Lock, label: "Security & Compliance", desc: "Zero-trust, DLP & identity", color: "hsl(var(--success))" },
  { icon: BarChart3, label: "Strategy & OKR", desc: "Measure outcomes & ROI", color: "hsl(var(--primary-glow))" },
];

const SmartArtSlide = ({ thumbnail }: { thumbnail?: boolean }) => (
  <div className="relative w-full h-full flex flex-col items-center justify-center bg-background overflow-hidden">
    <MovingBackground variant="mesh" />

    <div className="relative z-10 w-full max-w-5xl px-8">
      <motion.div
        className="text-center mb-8 md:mb-12"
        initial={thumbnail ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease }}
      >
        <p className="label-text mb-2" style={{ fontSize: thumbnail ? "0.5rem" : undefined }}>
          Service Framework
        </p>
        <h2
          className="font-display font-bold text-foreground"
          style={{ fontSize: thumbnail ? "0.9rem" : "clamp(1.5rem, 3vw, 2.5rem)" }}
        >
          Six Pillars of <span className="text-gradient">AI Transformation</span>
        </h2>
      </motion.div>

      {/* Hexagonal-ish grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {pillars.map((pillar, i) => (
          <motion.div
            key={pillar.label}
            className="relative group"
            initial={thumbnail ? false : { opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.15 * i, duration: 0.6, ease }}
          >
            <div className="glow-border rounded-xl p-4 md:p-6 bg-card/80 backdrop-blur-sm border border-border/50 h-full">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                style={{ background: `${pillar.color}15` }}
              >
                <pillar.icon size={thumbnail ? 10 : 20} style={{ color: pillar.color }} />
              </div>
              <h3
                className="font-display font-semibold text-foreground mb-1"
                style={{ fontSize: thumbnail ? "0.5rem" : "clamp(0.85rem, 1.2vw, 1.1rem)" }}
              >
                {pillar.label}
              </h3>
              {!thumbnail && (
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {pillar.desc}
                </p>
              )}
            </div>

            {/* Connector lines for non-thumbnail */}
            {!thumbnail && i < pillars.length - 1 && i !== 2 && (
              <motion.div
                className="hidden md:block absolute top-1/2 -right-3 w-6 h-px"
                style={{ background: "var(--gradient-primary)" }}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.3 + i * 0.15, duration: 0.4, ease }}
              />
            )}
          </motion.div>
        ))}
      </div>
    </div>
  </div>
);

export default SmartArtSlide;
